<template>
  <div class="resource-page specialist-page">
    <PageHeader v-if="!embedded" icon="fas fa-box-open"
      ><template #title>{{
        definition ? definition.label : "Katalog & operasional"
      }}</template
      ><template #subtitle
        >Kelola paket, batch, program, dan data operasional dengan riwayat versi
        yang terlindungi.</template
      ><template #actions
        ><button
          v-if="canEdit && definition"
          class="btn specialist-primary-action"
          @click="newResource"
        >
          <i class="fas fa-plus mr-2"></i>Data baru
        </button></template
      ></PageHeader
    >
    <b-alert v-if="errorMessage" show variant="danger" class="mt-3">{{
      errorMessage
    }}</b-alert>
    <div
      v-if="embedded && definition"
      class="embedded-heading specialist-panel"
    >
      <div>
        <span>KATALOG & OPERASIONAL</span>
        <h2>{{ definition.label }}</h2>
        <p>
          Kelola data pendukung untuk
          {{ verticalLabel(definition.vertical) }} tanpa meninggalkan workspace
          layanan.
        </p>
      </div>
      <button
        v-if="canEdit"
        class="btn specialist-primary-action"
        @click="newResource"
      >
        <i class="fas fa-plus mr-2"></i>Data baru
      </button>
    </div>
    <WorkspaceTabs
      v-if="showTabs && activeType && definitions.length > 1"
      :value="activeType"
      :items="resourceTabs"
      :aria-label="`Katalog ${verticalLabel(effectiveVertical)}`"
      @change="selectType"
    />
    <div v-if="definition" class="resource-grid">
      <aside>
        <button
          v-for="item in resources"
          :key="item.id"
          :class="{ active: resource && resource.id === item.id }"
          @click="open(item.id)"
        >
          <span>{{ item.resource_code }} · v{{ item.version }}</span
          ><strong>{{ item.name }}</strong
          ><small :class="`state-${item.status}`">{{
            statusLabel(item.status)
          }}</small>
        </button>
        <p v-if="loading">
          Memuat data katalog…
        </p>
        <p v-else-if="!resources.length">
          Belum ada data. Gunakan tombol “Data baru” untuk mulai.
        </p>
      </aside>
      <main>
        <div v-if="resource" class="editor">
          <div class="editor-heading">
            <div>
              <span>{{ verticalLabel(definition.vertical) }}</span>
              <h2>
                {{ resource.id ? resource.name : "Data operasional baru" }}
              </h2>
            </div>
            <div>
              <b-badge
                :variant="
                  resource.status === 'active'
                    ? 'success'
                    : resource.status === 'draft'
                    ? 'warning'
                    : 'secondary'
                "
                >{{ statusLabel(resource.status) }}</b-badge
              ><button
                v-if="editable"
                class="btn btn-light-primary ml-2"
                @click="save"
              >
                Simpan draft</button
              ><button
                v-if="resource.id && resource.status === 'draft' && canEdit"
                class="btn btn-primary ml-2"
                @click="transition('active')"
              >
                Aktifkan</button
              ><button
                v-if="resource.id && resource.status === 'active' && canEdit"
                class="btn btn-light-danger ml-2"
                @click="transition('retired')"
              >
                Nonaktifkan</button
              ><button
                v-if="resource.id && resource.status !== 'draft' && canEdit"
                class="btn btn-light-primary ml-2"
                @click="newVersion"
              >
                Versi baru
              </button>
            </div>
          </div>
          <div class="form-grid">
            <div>
              <label>Kode *</label
              ><input
                v-model="resource.resource_code"
                :disabled="!!resource.id"
                class="form-control"
              />
            </div>
            <div>
              <label>Nama *</label
              ><input
                v-model="resource.name"
                :disabled="!editable"
                class="form-control"
              />
            </div>
            <div>
              <label>Efektif mulai</label
              ><input
                v-model="resource.effective_from"
                :disabled="!editable"
                type="datetime-local"
                class="form-control"
              />
            </div>
            <div>
              <label>Efektif sampai</label
              ><input
                v-model="resource.effective_until"
                :disabled="!editable"
                type="datetime-local"
                class="form-control"
              />
            </div>
            <div
              v-for="field in definition.fields"
              :key="field.key"
              :class="{ wide: ['array', 'textarea', 'resource_quantity', 'billing_service_quantity', 'billing_service_items'].includes(field.type) }"
            >
              <label>{{ field.label }} <b v-if="field.required">*</b></label
              ><textarea
                v-if="field.type === 'array'"
                :value="arrayValue(field.key)"
                :disabled="!editable"
                rows="4"
                class="form-control"
                placeholder="Satu item per baris"
                @input="setArray(field.key, $event.target.value)"
              ></textarea
              ><textarea
                v-else-if="field.type === 'textarea'"
                v-model="resource.operational_payload[field.key]"
                :disabled="!editable"
                rows="4"
                class="form-control"
              ></textarea
              ><select
                v-else-if="field.type === 'billing_service'"
                v-model="resource.operational_payload[field.key]"
                :disabled="!editable"
                class="form-control"
                @change="applyBillingServiceDefaults(field)"
              >
                <option value="">Pilih layanan kasir</option>
                <option
                  v-for="service in billingServices"
                  :key="service.id"
                  :value="service.id"
                >
                  {{ service.name || service.label }} · Rp{{
                    Number(service.price || 0).toLocaleString("id-ID")
                  }}
                </option></select
              ><treeselect
                v-else-if="field.type === 'inventory_item'"
                v-model="resource.operational_payload[field.key]"
                :options="inventoryItems"
                :normalizer="inventoryNormalizer"
                :disabled="!editable"
                :clearable="editable"
                :searchable="true"
                placeholder="Cari produk di master Obat & Alkes"
              ></treeselect
              ><select
                v-else-if="field.type === 'select'"
                v-model="resource.operational_payload[field.key]"
                :disabled="!editable"
                class="form-control"
              >
                <option value="">Pilih</option>
                <option v-for="(label, value) in field.options" :key="value" :value="value">{{ label }}</option>
              </select
              ><select
                v-else-if="field.type === 'resource_reference'"
                v-model="resource.operational_payload[field.key]"
                :disabled="!editable"
                class="form-control"
              >
                <option value="">Pilih {{ field.label.toLowerCase() }}</option>
                <option v-for="option in resourceOptions[field.resource_type] || []" :key="option.id" :value="option.id">{{ option.name }} · {{ option.resource_code }}</option>
              </select
              ><div v-else-if="field.type === 'resource_quantity'" class="resource-lines">
                <div v-for="(line, index) in resource.operational_payload[field.key]" :key="`${field.key}-${index}`" class="resource-line">
                  <select v-model="line.resource_id" :disabled="!editable" class="form-control">
                    <option value="">Pilih {{ referenceLabel(field.resource_type) }}</option>
                    <option v-for="option in resourceOptions[field.resource_type] || []" :key="option.id" :value="option.id">{{ option.name }} · {{ option.resource_code }}</option>
                  </select>
                  <input v-model.number="line.quantity" :disabled="!editable" type="number" min="0.0001" step="any" class="form-control" :aria-label="field.quantity_label || 'Jumlah'" />
                  <input v-model="line.unit" :disabled="!editable" class="form-control" :placeholder="field.quantity_label === 'Sesi' ? 'sesi' : 'satuan'" />
                  <button v-if="editable" type="button" class="btn btn-light-danger" @click="removeResourceLine(field.key, index)" aria-label="Hapus item"><i class="fas fa-trash"></i></button>
                </div>
                <button v-if="editable" type="button" class="btn btn-light-primary" @click="addResourceLine(field)"><i class="fas fa-plus mr-1"></i>Tambah {{ referenceLabel(field.resource_type) }}</button>
              </div
              ><div v-else-if="field.type === 'billing_service_items'" class="service-lines dental-service-lines">
                <div class="dental-service-line service-line-heading"><span>Layanan Kasir</span><span>Harga master</span><span>Jumlah</span><span>Durasi</span><span>Alokasi paket</span><span></span></div>
                <div v-for="(line, index) in resource.operational_payload[field.key]" :key="`${field.key}-${index}`" class="dental-service-line">
                  <treeselect v-model="line.payment_category_id" :options="dentalBillingServices" :normalizer="billingServiceNormalizer" :disabled="!editable" :clearable="editable" :searchable="true" placeholder="Cari tindakan dental dari master Kasir" @input="syncBillingServiceSnapshot(line)" />
                  <div class="master-price"><strong>{{ money(serviceMasterPrice(line)) }}</strong><small v-if="line.master_price_snapshot && Number(line.master_price_snapshot) !== serviceMasterPrice(line)">Snapshot {{ money(line.master_price_snapshot) }}</small></div>
                  <input v-model.number="line.quantity" :disabled="!editable" type="number" min="1" class="form-control" aria-label="Jumlah tindakan" />
                  <div class="input-suffix"><input v-model.number="line.duration_minutes" :disabled="!editable" type="number" min="1" class="form-control" aria-label="Durasi estimasi" placeholder="Opsional" /><span>menit</span></div>
                  <input v-model.number="line.allocated_amount" :disabled="!editable" type="number" min="0" step="any" class="form-control" placeholder="Otomatis" aria-label="Alokasi nilai paket" />
                  <input v-model="line.notes" :disabled="!editable" maxlength="1000" class="form-control service-notes" placeholder="Catatan klinis opsional: gigi, area, atau ketentuan tindakan" aria-label="Catatan klinis" />
                  <button v-if="editable" type="button" class="btn btn-light-danger" @click="removeResourceLine(field.key, index)" aria-label="Hapus tindakan"><i class="fas fa-trash"></i></button>
                </div>
                <div class="service-summary"><span>Total {{ serviceQuantityTotal(field.key) }} {{ activeType === 'eye_service_package' ? 'komponen layanan' : 'tindakan' }}</span><span>Nilai master {{ money(serviceMasterTotal(field.key)) }}</span><span>Alokasi {{ money(serviceAllocationTotal(field.key)) }} / {{ money(resource.operational_payload.price) }}</span></div>
                <small class="text-muted">Pilih beberapa layanan Kasir. Alokasi boleh dikosongkan seluruhnya agar harga paket dibagi proporsional, atau diisi seluruh baris dengan total sama dengan harga paket.</small>
                <button v-if="editable" type="button" class="btn btn-light-primary" @click="addDentalServiceLine(field.key)"><i class="fas fa-plus mr-1"></i>Tambah {{ activeType === 'eye_service_package' ? 'layanan' : 'tindakan' }} dari master Kasir</button>
              </div
              ><div v-else-if="field.type === 'billing_service_quantity'" class="service-lines">
                <div class="service-line service-line-heading"><span>Layanan kasir</span><span>Jumlah sesi</span><span>Durasi</span><span>Alokasi nilai</span><span></span></div>
                <div v-for="(line, index) in resource.operational_payload[field.key]" :key="`${field.key}-${index}`" class="service-line">
                  <select v-model="line.payment_category_id" :disabled="!editable" class="form-control">
                    <option value="">Pilih layanan kasir</option>
                    <option v-for="service in billingServices" :key="service.id" :value="service.id">{{ service.name || service.label }}</option>
                  </select>
                  <input v-model.number="line.sessions" :disabled="!editable" type="number" min="1" class="form-control" aria-label="Jumlah sesi" />
                  <div class="input-suffix"><input v-model.number="line.duration_minutes" :disabled="!editable" type="number" min="1" class="form-control" aria-label="Durasi" /><span>menit</span></div>
                  <input v-model.number="line.allocated_amount" :disabled="!editable" type="number" min="0" step="any" class="form-control" placeholder="Otomatis" aria-label="Alokasi nilai" />
                  <button v-if="editable" type="button" class="btn btn-light-danger" @click="removeResourceLine(field.key, index)" aria-label="Hapus layanan"><i class="fas fa-trash"></i></button>
                </div>
                <div class="service-summary"><span>Total {{ serviceSessionTotal(field.key) }} sesi</span><span>Alokasi {{ money(serviceAllocationTotal(field.key)) }} / {{ money(resource.operational_payload.price) }}</span></div>
                <small class="text-muted">Kosongkan seluruh alokasi nilai agar sistem membaginya otomatis secara proporsional.</small>
                <button v-if="editable" type="button" class="btn btn-light-primary" @click="addBillingServiceLine(field.key)"><i class="fas fa-plus mr-1"></i>Tambah layanan kasir</button>
              </div
              ><input
                v-else
                v-model="resource.operational_payload[field.key]"
                :disabled="!editable"
                :type="inputType(field.type)"
                :min="field.min"
                :max="field.max"
                :step="field.type === 'number' ? 'any' : null"
                class="form-control"
              />
              <small v-if="field.type === 'inventory_item'" class="field-guidance">Produk, kode/barcode, kategori, dan satuan bersumber dari master Obat & Alkes.</small>
              <small v-if="field.key === 'payment_category_id' && ['eye_frame', 'eye_lens'].includes(activeType)" class="field-guidance">Mapping ini hanya untuk harga dan baris invoice. Relasinya harus menunjuk produk Obat & Alkes yang sama.</small>
            </div>
          </div>
          <details v-if="resource.events && resource.events.length">
            <summary>Jejak audit ({{ resource.events.length }})</summary>
            <p v-for="event in resource.events" :key="event.id">
              <strong>{{ event.event_type }}</strong> — {{ event.reason }}
            </p>
          </details>
        </div>
      </main>
    </div>
  </div>
</template>
<script>
import ApiService from "@/core/services/api.service.js";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import WorkspaceTabs from "@/component/general/WorkspaceTabs.vue";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
export default {
  name: "VerticalResourceWorkspace",
  components: { WorkspaceTabs, Treeselect },
  props: {
    verticalFilter: { type: String, default: null },
    resourceType: { type: String, default: null },
    showTabs: { type: Boolean, default: true },
    embedded: { type: Boolean, default: false },
  },
  data: () => ({
    registry: {},
    activeType: null,
    resources: [],
    resource: null,
    billingServices: [],
    inventoryItems: [],
    resourceOptions: {},
    errorMessage: "",
    loading: false,
  }),
  computed: {
    routeVertical() {
      const type = this.$route.params.resourceType;
      return type && this.registry[type] ? this.registry[type].vertical : null;
    },
    effectiveVertical() {
      return this.verticalFilter || this.routeVertical;
    },
    definitions() {
      return Object.keys(this.registry)
        .map((code) => ({ code, ...this.registry[code] }))
        .filter(
          (item) =>
            !this.effectiveVertical || item.vertical === this.effectiveVertical
        );
    },
    resourceTabs() {
      return this.definitions.map((item) => ({
        key: item.code,
        label: item.label,
        icon: this.resourceIcon(item.code),
      }));
    },
    definition() {
      return this.registry[this.activeType];
    },
    canEdit() {
      let r = [];
      try {
        r = JSON.parse(localStorage.getItem("access_right_display")) || [];
      } catch (e) {}
      return r.length === 0 || r.map(String).includes("1202");
    },
    editable() {
      return this.resource && this.resource.status === "draft" && this.canEdit;
    },
    dentalBillingServices() {
      if (this.activeType !== "dental_treatment_package") return this.billingServices;
      const selected = new Set(((this.resource && this.resource.operational_payload && this.resource.operational_payload.procedures) || []).map((line) => Number(line.payment_category_id)).filter(Boolean));
      return this.billingServices.filter((service) => {
        const units = service.service_unit_codes || [];
        return units.includes("dental") || units.includes("shared") || selected.has(Number(service.id));
      });
    },
  },
  async mounted() {
    if (!this.embedded)
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: "Layanan Spesialis" },
        { title: "Katalog & Operasional" },
      ]);
    await Promise.all([this.loadRegistry(), this.loadInventoryItems()]);
  },
  watch: {
    "$route.params.resourceType"(v) {
      if (!this.embedded && v && this.registry[v]) this.selectType(v);
    },
    verticalFilter() {
      this.chooseInitialType();
    },
    resourceType() {
      this.chooseInitialType();
    },
  },
  methods: {
    async loadRegistry() {
      try {
        const r = await ApiService.get("vertical-resources/registry");
        this.registry = r.data.data || {};
        await this.chooseInitialType();
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    async loadBillingServices() {
      try {
        if (!this.definition) {
          this.billingServices = [];
          return;
        }
        const r = await ApiService.query("payment-categories", {
          params: {
            page_size: 500,
            name: "",
            unit_code: this.definition.vertical,
            include_shared: 1,
            include_ids: JSON.stringify(this.selectedBillingServiceIds()),
          },
        });
        this.billingServices = this.items(r.data);
        this.normalizeLegacyDentalPackage();
        this.normalizeLegacyEyePackage();
      } catch (e) {
        this.billingServices = [];
      }
    },
    async loadInventoryItems() {
      try {
        const response = await ApiService.query("inventories", { params: { page_size: 500, name: "" } });
        this.inventoryItems = this.items(response.data);
      } catch (e) {
        this.inventoryItems = [];
      }
    },
    async chooseInitialType() {
      const requested = this.resourceType || this.$route.params.resourceType;
      const allowed = this.definitions.map((item) => item.code);
      if (requested && !allowed.includes(requested)) {
        this.activeType = null;
        this.resource = null;
        this.resources = [];
        this.errorMessage = "Jenis master data yang diminta tidak tersedia untuk produk klinik ini.";
        return;
      }
      this.activeType = (requested || allowed[0]) || null;
      this.resource = null;
      this.resources = [];
      if (this.activeType) {
        await this.loadBillingServices();
        await this.loadReferenceOptions();
        await this.load();
      }
    },
    async selectType(code) {
      this.activeType = code;
      this.resource = null;
      if (!this.embedded && this.$route.params.resourceType !== code)
        this.$router.replace(`/vertical-resources/${code}`);
      this.$emit("change", code);
      await this.loadBillingServices();
      await this.loadReferenceOptions();
      await this.load();
    },
    async load() {
      this.loading = true;
      try {
        const r = await ApiService.query("vertical-resources", {
          params: { resource_type: this.activeType, page_size: 100 },
        });
        this.resources = this.items(r.data);
        if (this.resources.length) await this.open(this.resources[0].id);
        else this.newResource();
      } catch (e) {
        this.resources = [];
        this.resource = null;
        this.errorMessage = this.error(e);
      } finally {
        this.loading = false;
      }
    },
    items(p) {
      if (Array.isArray(p.data)) return p.data;
      if (p.data && Array.isArray(p.data.data)) return p.data.data;
      if (Array.isArray(p.items)) return p.items;
      return [];
    },
    newResource() {
      const payload = {};
      this.definition.fields.forEach(
        (f) =>
          (payload[f.key] = ["array", "resource_quantity", "billing_service_quantity", "billing_service_items"].includes(f.type)
            ? []
            : "")
      );
      this.resource = {
        id: null,
        resource_code: "",
        name: "",
        version: 1,
        status: "draft",
        operational_payload: payload,
        effective_from: null,
        effective_until: null,
        events: [],
      };
    },
    newVersion() {
      const previous = this.resource;
      this.newResource();
      this.resource.resource_code = previous.resource_code;
      this.resource.name = previous.name;
      this.resource.operational_payload = JSON.parse(
        JSON.stringify(previous.operational_payload)
      );
      this.resource.effective_from = previous.effective_from;
      this.resource.effective_until = previous.effective_until;
    },
    async open(id) {
      const r = await ApiService.get(`vertical-resources/${id}`);
      this.resource = r.data.data;
      this.normalizeLegacyTherapyPackage();
      this.normalizeLegacyDentalPackage();
      this.normalizeLegacyEyePackage();
      await this.loadBillingServices();
      this.resource.effective_from = this.localDate(
        this.resource.effective_from
      );
      this.resource.effective_until = this.localDate(
        this.resource.effective_until
      );
    },
    selectedBillingServiceIds() {
      const ids = [];
      const visit = value => {
        if (Array.isArray(value)) return value.forEach(visit);
        if (!value || typeof value !== "object") return;
        if (value.payment_category_id) ids.push(Number(value.payment_category_id));
        Object.keys(value).forEach(key => visit(value[key]));
      };
      visit(this.resource && this.resource.operational_payload);
      return [...new Set(ids.filter(Boolean))];
    },
    async save() {
      const reason = window.prompt("Alasan perubahan data:");
      if (!reason) return;
      try {
        const p = {
          resource_code: this.resource.resource_code,
          name: this.resource.name,
          operational_payload: this.resource.operational_payload,
          effective_from: this.resource.effective_from || null,
          effective_until: this.resource.effective_until || null,
          reason,
        };
        const r = this.resource.id
          ? await ApiService.put(`vertical-resources/${this.resource.id}`, p)
          : await ApiService.post(`vertical-resources/${this.activeType}`, p);
        this.resource = r.data.data;
        await this.loadList();
        this.toast("Data tersimpan.");
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    async transition(status) {
      const reason = window.prompt(
        `Alasan perubahan status ke ${this.statusLabel(status)}:`
      );
      if (!reason) return;
      try {
        const r = await ApiService.post(
          `vertical-resources/${this.resource.id}/transition`,
          { status, reason }
        );
        this.resource = r.data.data;
        await this.loadList();
        this.toast("Status data diperbarui.");
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    async loadList() {
      const r = await ApiService.query("vertical-resources", {
        params: { resource_type: this.activeType, page_size: 100 },
      });
      this.resources = this.items(r.data);
    },
    arrayValue(k) {
      const v = this.resource.operational_payload[k];
      return Array.isArray(v) ? v.join("\n") : v || "";
    },
    setArray(k, v) {
      this.$set(
        this.resource.operational_payload,
        k,
        v
          .split("\n")
          .map((x) => x.trim())
          .filter(Boolean)
      );
    },
    async loadReferenceOptions() {
      if (!this.definition) return;
      const types = [...new Set(this.definition.fields.filter((field) => field.resource_type).map((field) => field.resource_type))];
      await Promise.all(types.map(async (type) => {
        try {
          const response = await ApiService.query("vertical-resources", { params: { resource_type: type, status: "active", available_now: 1, page_size: 100 } });
          this.$set(this.resourceOptions, type, this.items(response.data));
        } catch (e) {
          this.$set(this.resourceOptions, type, []);
        }
      }));
    },
    addResourceLine(field) {
      if (!Array.isArray(this.resource.operational_payload[field.key])) this.$set(this.resource.operational_payload, field.key, []);
      this.resource.operational_payload[field.key].push({ resource_id: "", quantity: 1, unit: field.quantity_label === "Sesi" ? "sesi" : "" });
    },
    removeResourceLine(key, index) {
      this.resource.operational_payload[key].splice(index, 1);
    },
    addBillingServiceLine(key) {
      if (!Array.isArray(this.resource.operational_payload[key])) this.$set(this.resource.operational_payload, key, []);
      this.resource.operational_payload[key].push({ payment_category_id: "", sessions: 1, duration_minutes: 60, allocated_amount: null });
    },
    addDentalServiceLine(key) {
      if (!Array.isArray(this.resource.operational_payload[key])) this.$set(this.resource.operational_payload, key, []);
      this.resource.operational_payload[key].push({ payment_category_id: "", quantity: 1, duration_minutes: null, allocated_amount: null, notes: "" });
    },
    billingService(id) {
      return this.billingServices.find((service) => Number(service.id) === Number(id));
    },
    applyBillingServiceDefaults(field) {
      if (this.activeType !== "eye_service_package" || field.key !== "payment_category_id") return;
      const service = this.billingService(this.resource.operational_payload[field.key]);
      this.$set(this.resource.operational_payload, "price", Number(service ? service.price : 0));
    },
    billingServiceNormalizer(service) {
      return { id: service.id, label: `${service.name || service.label} · ${this.money(service.price)}` };
    },
    inventoryNormalizer(item) {
      const code = item.inventory_code || item.product_number || `#${item.id}`;
      const category = item.inventory_category_name || "Tanpa kategori";
      return { id: item.id, label: `${item.name} · ${code} · ${category} · stok ${Number(item.inventory_remaining_stock || 0).toLocaleString("id-ID")}` };
    },
    syncBillingServiceSnapshot(line) {
      const service = this.billingService(line.payment_category_id);
      if (!service) return;
      this.$set(line, "service_name_snapshot", service.name || service.label);
      this.$set(line, "master_price_snapshot", Number(service.price || 0));
      this.$delete(line, "legacy_description");
    },
    serviceMasterPrice(line) {
      const service = this.billingService(line.payment_category_id);
      return Number(service ? service.price : line.master_price_snapshot || 0);
    },
    normalizeLegacyDentalPackage() {
      if (this.activeType !== "dental_treatment_package" || !this.resource || !this.resource.operational_payload) return;
      const procedures = this.resource.operational_payload.procedures;
      if (!Array.isArray(procedures)) return;
      const normalized = procedures.map((entry) => {
        if (entry && typeof entry === "object") return { quantity: 1, duration_minutes: null, allocated_amount: null, notes: "", ...entry };
        const description = String(entry || "").trim();
        const needle = description.toLowerCase();
        const service = this.billingServices.find((item) => String(item.name || item.label || "").toLowerCase() === needle) || this.billingServices.find((item) => {
          const name = String(item.name || item.label || "").toLowerCase();
          return needle && (name.includes(needle) || needle.includes(name));
        });
        return { payment_category_id: service ? service.id : "", quantity: 1, duration_minutes: null, allocated_amount: null, notes: "", legacy_description: service ? undefined : description, service_name_snapshot: service ? (service.name || service.label) : description, master_price_snapshot: service ? Number(service.price || 0) : 0 };
      });
      this.$set(this.resource.operational_payload, "procedures", normalized);
    },
    normalizeLegacyEyePackage() {
      if (this.activeType !== "eye_service_package" || !this.resource || !this.resource.operational_payload) return;
      const payload = this.resource.operational_payload;
      if (Array.isArray(payload.service_items)) return;
      // Legacy packages only stored one cashier item and did not define their components.
      // Keep it as the parent package SKU; users explicitly choose the new component lines.
      this.$set(payload, "service_items", []);
    },
    normalizeLegacyTherapyPackage() {
      if (
        this.activeType !== "therapy_session_package" ||
        !this.resource ||
        !this.resource.operational_payload ||
        Array.isArray(this.resource.operational_payload.service_items)
      )
        return;
      const payload = this.resource.operational_payload;
      this.$set(
        payload,
        "service_items",
        payload.payment_category_id
          ? [
              {
                payment_category_id: payload.payment_category_id,
                sessions: Number(payload.sessions || 1),
                duration_minutes: Number(payload.session_duration_minutes || 60),
                allocated_amount: Number(payload.price || 0),
              },
            ]
          : []
      );
    },
    serviceSessionTotal(key) {
      return (this.resource.operational_payload[key] || []).reduce((sum, line) => sum + Number(line.sessions || 0), 0);
    },
    serviceAllocationTotal(key) {
      return (this.resource.operational_payload[key] || []).reduce((sum, line) => sum + Number(line.allocated_amount || 0), 0);
    },
    serviceQuantityTotal(key) {
      return (this.resource.operational_payload[key] || []).reduce((sum, line) => sum + Number(line.quantity || 0), 0);
    },
    serviceMasterTotal(key) {
      return (this.resource.operational_payload[key] || []).reduce((sum, line) => sum + (this.serviceMasterPrice(line) * Number(line.quantity || 0)), 0);
    },
    money(value) {
      return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(Number(value || 0));
    },
    referenceLabel(type) {
      return ({ aesthetic_treatment: "treatment", aesthetic_product: "produk", aesthetic_product_batch: "batch" }[type] || "item");
    },
    inputType(t) {
      return t === "date"
        ? "date"
        : t === "datetime"
        ? "datetime-local"
        : t === "integer" || t === "number"
        ? "number"
        : "text";
    },
    localDate(v) {
      if (!v) return null;
      const d = new Date(v);
      return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
    },
    resourceIcon(type) {
      return type.includes("batch")
        ? "fas fa-boxes"
        : type.includes("cold_chain")
        ? "fas fa-temperature-low"
        : type.includes("program")
        ? "fas fa-project-diagram"
        : "fas fa-box-open";
    },
    verticalLabel(code) {
      return (
        {
          dental: "Klinik Gigi",
          aesthetic: "Klinik Kecantikan",
          maternal_child: "Ibu & Anak",
          mcu: "Medical Check Up",
          eye: "Klinik Mata",
          physiotherapy: "Fisioterapi",
          occupational: "Kesehatan Kerja",
          vaccination: "Vaksinasi",
        }[code] || code
      );
    },
    statusLabel(status) {
      return (
        { draft: "Draft", active: "Aktif", retired: "Nonaktif" }[status] ||
        status
      );
    },
    toast(m) {
      this.$bvToast.toast(m, {
        title: "Berhasil",
        variant: "success",
        solid: true,
      });
    },
    error(e) {
      const d = e && e.response && e.response.data;
      if (d && d.errors) return Object.values(d.errors).flat()[0];
      return (d && (d.message || d.error)) || "Permintaan gagal.";
    },
  },
};
</script>
<style scoped>
.resource-page {
  padding-bottom: 2rem;
  color: #183153;
}
.resource-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.7rem 2rem;
  border-radius: 22px;
  background: linear-gradient(130deg, var(--skin-secondary), var(--skin-primary));
  color: #fff;
}
.resource-hero span {
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.14em;
}
.resource-hero h1 {
  font-size: 1.7rem;
  margin: 0.3rem 0;
}
.resource-hero p {
  margin: 0;
  color: #ede9fe;
}
.type-tabs {
  display: flex;
  gap: 0.6rem;
  margin: 1rem 0;
  overflow: auto;
}
.type-tabs button {
  display: flex;
  flex-direction: column;
  min-width: 190px;
  padding: 0.75rem 1rem;
  border: 1px solid #dbe7ef;
  border-radius: 12px;
  background: #fff;
  text-align: left;
}
.type-tabs button.active {
  border-color: var(--skin-secondary);
  background: var(--skin-soft);
}
.type-tabs small {
  color: #64748b;
  text-transform: uppercase;
}
.resource-lines {
  display: grid;
  gap: 0.65rem;
  padding: 0.8rem;
  border: 1px solid #dbe7ef;
  border-radius: 12px;
  background: #f8fafc;
}
.field-guidance { display: block; margin-top: 0.35rem; color: #64748b; font-size: 0.68rem; line-height: 1.4; }
.resource-line {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) 120px 100px auto;
  gap: 0.55rem;
  align-items: center;
}
.service-lines {
  display: grid;
  width: 100%;
  min-width: 0;
  gap: 0.65rem;
  padding: 0.9rem;
  box-sizing: border-box;
  border: 1px solid #dbe7ef;
  border-radius: 12px;
  background: #f8fafc;
}
.service-line {
  display: grid;
  grid-template-columns: minmax(160px, 1fr) 76px 112px 116px 42px;
  gap: 0.55rem;
  align-items: center;
  width: 100%;
  min-width: 0;
}
.service-line > * {
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}
.service-line > .btn {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  padding: 0;
}
.service-line-heading {
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 700;
}
.dental-service-line {
  display: grid;
  grid-template-columns: minmax(180px, 1.5fr) 105px 65px 100px 112px 42px;
  gap: 0.55rem;
  align-items: center;
  min-width: 0;
}
.dental-service-line > * { min-width: 0; }
.dental-service-line > .btn { display: grid; place-items: center; width: 42px; height: 42px; padding: 0; }
.dental-service-line > .service-notes { grid-column: 1 / 6; grid-row: 2; }
.dental-service-line > .btn { grid-column: 6; grid-row: 1; }
.master-price { display: flex; flex-direction: column; color: #183153; font-size: .72rem; }
.master-price small { color: #b45309; font-size: .6rem; }
.input-suffix { position: relative; }
.input-suffix input { padding-right: 3.4rem; }
.input-suffix span { position: absolute; right: 0.7rem; top: 50%; transform: translateY(-50%); color: #64748b; font-size: 0.68rem; }
.service-summary { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: .35rem 1rem; min-width: 0; padding-top: 0.6rem; border-top: 1px dashed #cbd5e1; color: var(--skin-primary); font-weight: 700; }
.service-summary span { min-width: 0; }
.service-summary span:last-child { text-align: right; }
.service-lines > .btn { width: 100%; max-width: 100%; box-sizing: border-box; }
@media (max-width: 1200px) {
  .service-line {
    grid-template-columns: minmax(140px, 1fr) 68px 104px 105px 40px;
    gap: 0.4rem;
  }
  .service-line > .btn { width: 40px; height: 40px; }
}
@media (max-width: 900px) {
  .resource-line { grid-template-columns: 1fr 1fr; }
  .service-line { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
  .service-line-heading { display: none; }
  .dental-service-line { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
  .dental-service-line.service-line-heading { display: none; }
  .dental-service-line > .btn { width: 100%; grid-column: span 2; }
  .dental-service-line > .service-notes { grid-column: span 2; grid-row: auto; }
  .service-line > .btn { width: 100%; grid-column: span 2; }
}
.resource-grid {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 1rem;
  min-width: 0;
}
.resource-grid aside,
.resource-grid main {
  border: 1px solid #e3ebf1;
  border-radius: 16px;
  background: #fff;
  padding: 0.9rem;
  min-width: 0;
}
.resource-grid aside button {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 0.5rem;
  padding: 0.75rem;
  border: 1px solid transparent;
  border-radius: 10px;
  background: #f8fafc;
  text-align: left;
}
.resource-grid aside button.active {
  border-color: var(--skin-secondary);
  background: var(--skin-soft);
}
.resource-grid aside span,
.resource-grid aside small {
  font-size: 0.65rem;
  color: #64748b;
}
.editor-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0 1rem;
  border-bottom: 1px solid #edf2f4;
}
.editor-heading span {
  font-size: 0.65rem;
  text-transform: uppercase;
  color: var(--skin-primary);
}
.editor-heading h2 {
  margin: 0.2rem 0;
}
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding: 1rem 0;
}
.form-grid > * { min-width: 0; }
.form-grid label {
  font-size: 0.72rem;
  font-weight: 700;
}
.form-grid b {
  color: #dc2626;
}
.form-grid .wide {
  grid-column: span 2;
}
.state-active {
  color: #087f5b;
}
.state-draft {
  color: #b45309;
}
.state-retired {
  color: #64748b;
}
details {
  padding: 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
}
details summary {
  font-weight: 700;
  cursor: pointer;
}
@media (max-width: 800px) {
  .resource-grid {
    grid-template-columns: 1fr;
  }
  .resource-grid aside {
    display: flex;
    overflow: auto;
  }
  .resource-grid aside button {
    min-width: 160px;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
  .form-grid .wide {
    grid-column: auto;
  }
  .editor-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 1rem;
  }
}
@media (max-width: 560px) {
  .resource-hero {
    align-items: flex-start;
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
<style scoped>
.embedded-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem 1.25rem;
  border: 1px solid #dbe7ef;
  border-radius: 14px;
  background: #fff;
}
.embedded-heading span {
  color: var(--skin-primary);
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.1em;
}
.embedded-heading h2 {
  margin: 0.15rem 0;
  font-size: 1.1rem;
}
.embedded-heading p {
  margin: 0;
  color: #52677a;
}
.resource-hero {
  background: linear-gradient(130deg, var(--skin-deep), var(--skin-primary));
}
.resource-hero p {
  color: #f5f3ff;
}
.resource-page .form-control:focus {
  border-color: var(--skin-primary);
  box-shadow: 0 0 0 3px rgba(var(--skin-primary-rgb), 0.14);
}
.resource-page .form-control:disabled {
  color: #40566a;
  opacity: 1;
}
.resource-grid aside span,
.resource-grid aside small {
  color: #52677a;
}
.type-tabs button:focus-visible,
.resource-grid aside button:focus-visible {
  outline: 3px solid rgba(var(--skin-primary-rgb), 0.24);
  outline-offset: 2px;
}
@media (max-width: 600px) {
  .embedded-heading {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
